<template>
  <SfModal
    v-if="modalOpen"
    class="period-selection-modal"
    :class="modalClass"
    size="m"
    :scrollable-content="false"
    @close="close"
  >
    <template #title
      >{{ I18n.t('companies_overview.period_selection_new.title') }}
    </template>

    <template #body>
      <SfInpageList>
        <PeriodSelectionItem
          v-for="periodSelectionOption in periodSelectionOptions"
          :key="periodSelectionOption.key"
          :period-selection-option="periodSelectionOption"
          :ledger-select-date="ledgerSelectDate"
          :ledger-select-lock-type="ledgerSelectLockType"
          :ledger-select-date-type="ledgerSelectDateType"
          :selected="periodSelection === periodSelectionOption.key"
          @item-click="changePeriodSelection(periodSelectionOption.key)"
          @date-type-selected="handleDateTypeSelected"
          @lock-type-selected="handleLockTypeSelected"
          @date-selected="handleDateSelected"
        />
      </SfInpageList>
    </template>

    <template #footer>
      <SfButtonDs
        variant="primary"
        :disabled="!validForSave"
        class="rs-save-period-selection"
        @click.prevent="save"
      >
        {{ I18n.t('common.save') }}
      </SfButtonDs>
    </template>
  </SfModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useVuelidate } from '@vuelidate/core'

import { SfModal } from '@app/shared/vue/components/sf-modal'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import { SfTime } from '@app/adapters/time'
import I18n from '@app/adapters/I18n'
import SfInpageList from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list.vue'
import PeriodSelectionItem from './period-selection-item.vue'

import type {
  DateType,
  LockType,
  PeriodSelectionData,
  PeriodSelectionType,
} from '../types'
import { dateTypes, lockTypes, periodSelectionTypes } from '../types'
import { periodSelectionOptions } from '../constants'

const modalOpen = ref<boolean>(false)
const periodSelection = ref<PeriodSelectionType>(
  periodSelectionTypes.FINANCIAL_YEAR
)
const ledgerSelectLockType = ref<LockType | undefined>(lockTypes.LOCKED)
const ledgerSelectDateType = ref<DateType | undefined>(dateTypes.TODAY)
const ledgerSelectDate = ref<string | null>(null)
const saveCallback =
  ref<
    | ((data: {
        periodSelection: string | null
        ledgerSelectDate: string | null
        ledgerSelectLockType: LockType | undefined
      }) => void)
    | null
  >(null)

// Validations
const validations = {
  ledgerSelectDate: {
    dateFormat: (value: string | null) => {
      // Empty (to allow clearing date) or DD/MM/YYYY
      if (value === null || value === '') {
        return true
      }
      return /^\d{2}\/\d{2}\/\d{4}$/.test(value)
    },
  },
}

const v$ = useVuelidate(validations, { ledgerSelectDate })

// Computed properties
const validForSave = computed<boolean>(() => {
  if (!periodSelection.value) {
    return false
  }
  return true
})

const currentDate = computed<string>(() => {
  return SfTime(new Date()).format('DD/MM/YYYY')
})

const modalClass = computed(() =>
  I18n.currentLocale().startsWith('en') ? '' : 'custom-size'
)

// Watchers
// Needed for vuelidate because it isn't triggered, probably because we pass it in v-model to datepicker
watch(ledgerSelectDate, () => {
  v$.value.ledgerSelectDate.$touch()
})

// Methods
// ModalCitizen interface
const openModal = (data: PeriodSelectionData): void => {
  periodSelection.value = data.periodSelection as PeriodSelectionType
  ledgerSelectLockType.value = data.ledgerSelectLockType || lockTypes.LOCKED
  ledgerSelectDate.value = data.ledgerSelectDate
  if (ledgerSelectDate.value) {
    ledgerSelectDateType.value = dateTypes.FIXED_DATE
  }
  ledgerSelectLockType.value = data.ledgerSelectLockType || lockTypes.LOCKED
  saveCallback.value = data.saveCallback
  modalOpen.value = true
}

const close = (): void => {
  modalOpen.value = false
}

const save = (): void => {
  if (saveCallback.value) {
    if (
      periodSelection.value === periodSelectionTypes.ACCOUNTING_PERIOD ||
      periodSelection.value === periodSelectionTypes.FINANCIAL_YEAR
    ) {
      ledgerSelectLockType.value = undefined
    }

    if (
      periodSelection.value === periodSelectionTypes.ACTIVE_PERIOD ||
      periodSelection.value === periodSelectionTypes.COMPLETED_PERIOD ||
      ledgerSelectDateType.value === dateTypes.TODAY
    ) {
      ledgerSelectDate.value = null
    }

    saveCallback.value({
      periodSelection: periodSelection.value,
      ledgerSelectDate: ledgerSelectDate.value,
      ledgerSelectLockType: ledgerSelectLockType.value,
    })
  }
  close()
}

const changePeriodSelection = (
  newPeriodSelection: PeriodSelectionType
): void => {
  periodSelection.value = newPeriodSelection
}

const handleLockTypeSelected = (lockType: LockType): void => {
  ledgerSelectLockType.value = lockType
}

const handleDateTypeSelected = (dateType: DateType | undefined): void => {
  ledgerSelectDateType.value = dateType
  if (dateType === dateTypes.FIXED_DATE && !ledgerSelectDate.value) {
    ledgerSelectDate.value = currentDate.value
  }
}

const handleDateSelected = (date: string): void => {
  ledgerSelectDate.value = date
}

// Expose methods for external access (ModalCitizen interface)
defineExpose({
  openModal,
})
</script>

<style lang="scss" scoped>
@import 'silverfin-ui-kit/src/scss/tooling';

.date-selector input::placeholder {
  @include color('text');
}

.sf-period-description {
  height: 40px;
}

.sf-period-name {
  font-size: 16px !important;
}

.sf-period-selector-card {
  border-radius: 8px;
  border: 1px solid var(--neutral-light-1, #cbcbcb);
  width: 265px;
}

.sf-period-selector-card__selected {
  border-color: #0aa1ba;
}

.date-selector-item__caret {
  position: absolute;
  left: auto;
  right: 100px;
}
</style>

<style lang="scss">
.period-selection-modal.custom-size .sf-modal-inner {
  width: 940px;
  max-width: 940px;
}
</style>
